import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type PushNotificationSchema,
  type Token,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import { getMessagingClient, getWebPushToken } from '@/services/firebase';

/**
 * Unified push API. On iOS/Android the Capacitor plugin negotiates with
 * APNs / FCM. On web, we delegate to Firebase Messaging via the SDK.
 */
export function usePushNotifications() {
  const token = ref<string | null>(null);
  const error = ref<string | null>(null);
  const lastNotification = ref<PushNotificationSchema | null>(null);
  const lastAction = ref<ActionPerformed | null>(null);

  async function requestPermission(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        const status = await PushNotifications.requestPermissions();
        if (status.receive !== 'granted') return false;
        await PushNotifications.register();
        return true;
      }

      const messaging = await getMessagingClient();
      if (!messaging) return false;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;
      const t = await getWebPushToken();
      if (t) token.value = t;
      return true;
    } catch (e) {
      error.value = (e as Error).message;
      return false;
    }
  }

  function listen(handlers?: {
    onToken?: (t: string) => void;
    onReceived?: (n: PushNotificationSchema) => void;
    onAction?: (a: ActionPerformed) => void;
  }) {
    if (!Capacitor.isNativePlatform()) return;

    PushNotifications.addListener('registration', (t: Token) => {
      token.value = t.value;
      handlers?.onToken?.(t.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      error.value = JSON.stringify(err);
      console.error('[FCM] registration error:', JSON.stringify(err));
    });

    PushNotifications.addListener('pushNotificationReceived', (n) => {
      lastNotification.value = n;
      handlers?.onReceived?.(n);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
      lastAction.value = a;
      handlers?.onAction?.(a);
    });
  }

  return { token, error, lastNotification, lastAction, requestPermission, listen };
}
